import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useListNotificationsQuery,
  useMarkNotificationReadMutation
} from '@/services/api';
import { formatTimestamp } from '@/utils/format';
import { Meta } from '@/components/Meta';

const PAGE_SIZES = [10, 25, 50, 100] as const;

export function NotificationsPage(): JSX.Element {
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [page, setPage] = useState(0);

  const { data, isLoading, isFetching } = useListNotificationsQuery({
    page_size: pageSize,
    offset: page * pageSize
  });
  const [markRead] = useMarkNotificationReadMutation();

  const busy = isLoading || isFetching;

  const lastLoaded = useRef<typeof data>(undefined);
  if (data !== undefined) lastLoaded.current = data;
  const shown = data ?? lastLoaded.current;

  const total = shown?.total ?? shown?.notifications.length ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const pageStart = page * pageSize;

  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1);
  }, [page, pageCount]);

  const items = data?.notifications ?? [];

  return (
    <>
      <h1 className="page-title">Notifications</h1>
      {busy ? (
        <NotificationsSkeleton rows={pageSize} />
      ) : items.length === 0 ? (
        <div className="card" style={{ marginTop: 20 }}>
          <div className="empty" style={{ padding: '64px 24px' }}>
            <p className="empty-title">No notifications</p>
            <p className="empty-sub">SLA breach alerts for your projects appear here.</p>
          </div>
        </div>
      ) : (
        <div className="card" style={{ marginTop: 20, padding: 0 }}>
          {items.map((n) => (
            <div
              key={n.notification_id}
              style={{
                display: 'flex',
                gap: 14,
                padding: '14px 18px',
                borderBottom: '1px solid var(--border)',
                background: n.read_at ? 'transparent' : 'var(--surface-alt)'
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: 'var(--red)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5
                    }}
                  >
                    SLA breached
                  </span>
                  {n.project_name && <Meta label="project" value={n.project_name} />}
                </div>
                <div style={{ fontSize: 13, marginBottom: 8 }}>{n.body}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 8px', fontSize: 11.5 }}>
                  {n.pipeline_run_id && <Meta label="run" value={n.pipeline_run_id} mono />}
                  {n.stage_role && <Meta label="stage" value={n.stage_role} />}
                  {n.sla_minutes !== undefined && <Meta label="SLA" value={`${n.sla_minutes} min`} />}
                  {n.elapsed_seconds !== undefined && (
                    <Meta label="waited" value={`${Math.round(n.elapsed_seconds / 60)} min`} />
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-start', gap: 8, flexShrink: 0 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {formatTimestamp(n.created_at)}
                </span>
                {n.project_slug && n.pipeline_run_id && (
                  <Link
                    className="btn sm"
                    to={`/p/${n.project_slug}/runs/${encodeURIComponent(n.pipeline_run_id)}`}
                    onClick={() => {
                      if (!n.read_at) void markRead(n.notification_id);
                    }}
                    data-testid="notification-view-run"
                  >
                    View run →
                  </Link>
                )}
                {!n.read_at && (
                  <button
                    type="button"
                    style={{ fontSize: 11.5, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 0 }}
                    onClick={() => void markRead(n.notification_id)}
                  >
                    Mark read
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {total > 0 || busy ? (
        <div className="card" style={{ marginTop: 0 }}>
          <div className="pagination">
            <span className="pager-info">
              {busy
                ? 'loading…'
                : `${pageStart + 1}–${pageStart + items.length} of ${total}`}
            </span>
            <div className="pager-controls" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span className="mono-cap">Per page</span>
              <select
                className="select sm-sel"
                style={{ width: 80 }}
                aria-label="Notifications per page"
                disabled={busy}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(0);
                }}
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <button
                className="btn ghost sm"
                disabled={busy || page === 0}
                onClick={() => setPage((n) => Math.max(0, n - 1))}
              >
                ← Prev
              </button>
              <span className="pager-info">
                Page {page + 1} of {pageCount}
              </span>
              <button
                className="btn ghost sm"
                disabled={busy || page >= pageCount - 1}
                onClick={() => setPage((n) => Math.min(pageCount - 1, n + 1))}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function NotificationsSkeleton({ rows }: { rows: number }): JSX.Element {
  return (
    <div
      className="card"
      style={{ marginTop: 20, padding: 0 }}
      aria-busy="true"
      role="status"
      aria-label="Loading notifications"
    >
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            gap: 14,
            padding: '14px 18px',
            borderBottom: '1px solid var(--border)'
          }}
        >
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="animate-skel" style={bar('30%', 10)} />
            <div className="animate-skel" style={bar(`${55 + (i % 3) * 10}%`, 12)} />
            <div className="animate-skel" style={bar('45%', 10)} />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 8,
              flexShrink: 0
            }}
          >
            <div className="animate-skel" style={bar('110px', 10)} />
            <div className="animate-skel" style={bar('88px', 26)} />
          </div>
        </div>
      ))}
    </div>
  );
}

function bar(width: string, height: number): React.CSSProperties {
  return { width, height, borderRadius: 3, background: 'var(--surface-sunken)' };
}
